<script lang="ts" setup>
import { inject, onUnmounted, h, render } from 'vue';
import {
	clusterKey as clusterKeySymbol,
	mapPromiseKey
} from '@/core/service/googleMaps/utils/symbols';
import type {
	MapInstanceRef,
	MarkerInstanceRef
} from '@/core/service/googleMaps/types';
import {
	bindEvents,
	bindProps,
	getComponentEventsConfig,
	getComponentPropsConfig,
	getPropsValuesWithoutOptionsProp
} from '@/core/service/googleMaps/utils';
import { useNuxtApp } from '#app';
import IconMaterialSymbolsLocationOn from '~icons/material-symbols-light/location-on';

defineOptions({ name: 'GmapMarker' });

/**
 * Marker component
 * @displayName GmvMarker
 * @see [source code](/guide/marker.html#source-code)
 * @see [Official documentation](https://developers.google.com/maps/documentation/javascript/markers)
 * @see [Official reference](https://developers.google.com/maps/documentation/javascript/reference/marker)
 */

const props = withDefaults(
	defineProps<{
		collisionBehavior?: google.maps.CollisionBehavior;
		content?: HTMLElement;
		gmpClickable?: boolean;
		gmpDraggable?: boolean;
		position?:
			| google.maps.LatLng
			| google.maps.LatLngLiteral
			| google.maps.LatLngAltitude
			| google.maps.LatLngAltitudeLiteral;
		title?: string;
		zIndex?: number;
		markerKey?: string;
		clusterKey?: string;
		options?: Record<string, unknown>;
	}>(),
	{
		gmpClickable: true,
		gmpDraggable: true
	}
);

const emits = defineEmits<{
	click: [value: google.maps.MapMouseEvent];
	drag: [value: google.maps.MapMouseEvent];
	dragEnd: [value: google.maps.MapMouseEvent];
	dragStart: [value: google.maps.MapMouseEvent];
	gmpClick: [value: google.maps.marker.AdvancedMarkerClickEvent];
	'update:position': [value: { lat?: number; lng?: number }];
}>();

const mapPromise = inject(mapPromiseKey);
const mapInstance: MapInstanceRef = ref();
const clusterInstance = inject(clusterKeySymbol, ref());
const markerInstance: MarkerInstanceRef = ref();
const { $googleMapsApi } = useNuxtApp();
const isGoogleMapsLoaded = ref(false);

function createCustomMarker() {
	if (typeof document === 'undefined') return null;

	const container = document.createElement('div');

	const iconVNode = h(IconMaterialSymbolsLocationOn, {
		style: {
			width: '36x',
			height: '36px',
			color: '#000000',
			fill: '#000000',
			fontSize: '36px'
		}
	});

	render(iconVNode, container);
	return container;
}

async function initMarker() {
	try {
		if (!$googleMapsApi) {
			console.warn('Google Maps API not available yet');
			return;
		}

		isGoogleMapsLoaded.value = true;

		const options = {
			map: clusterInstance.value ? undefined : toRaw(mapInstance.value),
			...getPropsValuesWithoutOptionsProp(props),
			...props.options
		};

		const { AdvancedMarkerElement } = $googleMapsApi.marker;

		const markerContent = props.content || createCustomMarker();

		markerInstance.value = new AdvancedMarkerElement({
			...options,
			gmpDraggable: false,
			content: markerContent
		});

		bindComponent();

		if (clusterInstance.value && markerInstance.value) {
			clusterInstance.value.addMarker(markerInstance.value);
		}

		setupEventListeners();
	} catch (error) {
		console.error('Error initializing marker:', error);
	}
}

function bindComponent() {
	if (!markerInstance.value) return;

	const markerIconPropsConfig = getComponentPropsConfig('GmpMarker');
	const markerIconEventsConfig = getComponentEventsConfig('GmpMarker', 'auto');

	bindProps(markerIconPropsConfig, markerInstance.value, emits as any, props);

	bindEvents(markerIconEventsConfig, markerInstance.value, emits as any);
}

function setupEventListeners() {
	if (!markerInstance.value) return;

	markerInstance.value.addListener('dragend', () => {
		const newPosition = markerInstance.value?.position;
		/**
		 * An event to detect when a position changes
		 * @property {object} position Object with lat and lng values, eg: { lat: 10.0, lng: 10.0 }
		 */
		emits('update:position', {
			lat:
				typeof newPosition?.lat === 'number'
					? newPosition.lat
					: newPosition?.lat?.(),
			lng:
				typeof newPosition?.lng === 'number'
					? newPosition.lng
					: newPosition?.lng?.()
		});
	});

	if (mapInstance.value) {
		mapInstance.value.addListener('bounds_changed', () => {
			if (!markerInstance.value || clusterInstance.value) {
				return;
			}

			const bounds = mapInstance.value?.getBounds();
			const markerPosition = markerInstance.value.position;

			if (markerPosition && bounds) {
				const isMarkerInBounds = bounds.contains(markerPosition);
				markerInstance.value.map = isMarkerInBounds
					? toRaw(mapInstance.value)
					: null;
			}
		});
	}
}

onBeforeMount(async () => {
	try {
		mapInstance.value = await mapPromise;
		await initMarker();
	} catch (error) {
		console.error('Error mounting marker component:', error);
	}
});

onMounted(() => {
	// Re-attempt initialization if it failed during onBeforeMount
	if (!markerInstance.value && mapInstance.value && $googleMapsApi) {
		initMarker();
	}
});

onUnmounted(() => {
	try {
		if (markerInstance.value) {
			markerInstance.value.map = null;
		}
	} catch (error) {
		console.error('Error unmounting marker:', error);
	}
});

defineExpose({ markerInstance });
</script>

<template>
	<div></div>
</template>
